/**
 * VendorTaxSettings — the vendor's own tax and receipts panel (§17.6)
 * ─────────────────────────────────────────────────────────────────────────────
 * One panel, every vendor type, mounted on each profile screen the same way
 * <VendorAlertSettings /> is. Self-loading, self-saving, no props.
 *
 * ── WHY THE COPY IS THE FEATURE ──────────────────────────────────────────────
 * A vendor reading this screen is being asked to make a decision with a revenue
 * authority on the other end of it. Most of them are not accountants. So every
 * control here says what it does in the vendor's own terms, what happens if
 * they change it, and who is responsible afterwards — and the screen never uses
 * a word like "fiscalisation" without saying what it means.
 *
 * The three facts are kept visibly separate for the same reason the API keeps
 * them separate: they are genuinely independent, and a vendor who thinks having
 * a TIN means Lusuku is filing for them will not file, and will be assessed.
 *
 *   1. Do you charge VAT?          set by Lusuku, shown here
 *   2. Are we preparing receipts?  an entitlement, shown here
 *   3. Who files them with URA?    the vendor's own choice, editable here
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  apiGetMyVendorTaxSettings,
  apiUpdateMyVendorTaxSettings,
} from "../api";
import VendorFiscalCoveragePanel from "./fiscal/VendorFiscalCoveragePanel.js";

const C = {
  ink: "#0f2237",
  mid: "#51637a",
  light: "#8595a8",
  line: "#e2e8f0",
  card: "#ffffff",
  soft: "#f6f9fc",
  brand: "#013a63",
  good: "#00875a",
  goodSoft: "#e6faf7",
  warn: "#b7791f",
  warnSoft: "#fef6e7",
  info: "#2b6cb0",
  infoSoft: "#ebf4ff",
};

const S = {
  wrap: { display: "flex", flexDirection: "column", gap: 18 },
  band: (tone) => ({
    display: "flex",
    gap: 12,
    padding: "14px 16px",
    borderRadius: 10,
    background:
      tone === "good" ? C.goodSoft : tone === "warn" ? C.warnSoft : C.infoSoft,
    border: `1px solid ${
      tone === "good" ? "#b3ead9" : tone === "warn" ? "#f6e0b5" : "#c9def7"
    }`,
  }),
  bandTitle: (tone) => ({
    fontSize: 13.5,
    fontWeight: 700,
    color: tone === "good" ? C.good : tone === "warn" ? C.warn : C.info,
    marginBottom: 3,
  }),
  bandBody: { fontSize: 12.5, color: C.mid, lineHeight: 1.55 },
  factRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    padding: "13px 0",
    borderBottom: `1px solid ${C.line}`,
  },
  factLabel: { fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 3 },
  factHelp: { fontSize: 11.5, color: C.light, lineHeight: 1.5, maxWidth: 460 },
  pill: (tone) => ({
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    background: tone === "good" ? C.goodSoft : tone === "warn" ? C.warnSoft : "#eef2f7",
    color: tone === "good" ? C.good : tone === "warn" ? C.warn : C.mid,
  }),
  label: { display: "block", fontSize: 11.5, fontWeight: 700, color: C.mid, marginBottom: 6 },
  input: {
    width: "100%",
    maxWidth: 320,
    padding: "9px 11px",
    fontSize: 13,
    borderRadius: 8,
    border: `1px solid ${C.line}`,
    background: C.card,
    color: C.ink,
    outline: "none",
  },
  choice: (active) => ({
    display: "flex",
    gap: 11,
    alignItems: "flex-start",
    padding: "13px 14px",
    borderRadius: 10,
    cursor: "pointer",
    background: active ? C.soft : C.card,
    border: `1.5px solid ${active ? C.brand : C.line}`,
  }),
  choiceTitle: { fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 3 },
  choiceBody: { fontSize: 11.5, color: C.mid, lineHeight: 1.55 },
  btn: (disabled) => ({
    padding: "9px 18px",
    fontSize: 12.5,
    fontWeight: 700,
    borderRadius: 8,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    background: disabled ? "#cbd5e0" : C.brand,
    color: "#fff",
  }),
  note: { fontSize: 11.5, color: C.light, lineHeight: 1.55 },
};

const toneForStatus = (key) => {
  if (key === "LIVE_LUSUKU_FILES" || key === "LIVE_VENDOR_FILES") return "good";
  if (key === "NEEDS_TIN") return "warn";
  return "info";
};

export default function VendorTaxSettings() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tin, setTin] = useState("");
  const [remittance, setRemittance] = useState("VENDOR_SELF");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiGetMyVendorTaxSettings();
      setState(data);
      setTin(data?.efris?.tin || "");
      setRemittance(data?.efris?.remittance || "VENDOR_SELF");
      setError("");
    } catch (err) {
      // A vendor whose account is not linked to a shop is not an error state
      // worth shouting about — it is a support question, and the copy says so.
      setError(
        err?.response?.data?.message ||
          "Could not load your tax settings. Refresh, or contact Lusuku support."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiUpdateMyVendorTaxSettings({
        tin: tin.trim(),
        remittance,
      });
      setState(data);
      setTin(data?.efris?.tin || "");
      setRemittance(data?.efris?.remittance || "VENDOR_SELF");
      toast.success("Tax settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save your tax settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={S.note}>Loading your tax settings…</div>;
  if (error) return <div style={S.note}>{error}</div>;
  if (!state) return null;

  const dirty =
    tin.trim() !== (state.efris?.tin || "") || remittance !== state.efris?.remittance;
  const tone = toneForStatus(state.status?.key);
  /* A helper login (a cashier, a shop assistant) may read this and may not
     change it: the TIN is what every future document is filed under, and
     asking Lusuku to file is an authorisation only the owner can give. Said
     up front rather than discovered by pressing Save and being refused. */
  const canEdit = state.efris?.editableByVendor !== false;

  return (
    <div style={S.wrap}>
      {/* What is true right now, in one sentence. Computed on the server so the
          screen never re-derives it from three booleans and gets it wrong. */}
      <div style={S.band(tone)}>
        <div>
          <div style={S.bandTitle(tone)}>{state.status?.headline}</div>
          <div style={S.bandBody}>{state.status?.detail}</div>
        </div>
      </div>

      {/* ── FACT 1 and FACT 2: read-only, with the reason stated ───────── */}
      <div>
        <div style={S.factRow}>
          <div>
            <div style={S.factLabel}>Do you charge VAT?</div>
            <div style={S.factHelp}>
              {state.vat?.registered
                ? `Your menu prices already include ${state.vat.rateBps / 100}% VAT. Customers pay exactly the price you set; the tax is inside it, not added on top.`
                : "You are not VAT registered, so no VAT is charged on your sales and your receipts show no VAT line."}{" "}
              Lusuku sets this from your registration documents. Contact your
              Lusuku contact if it is wrong, because charging VAT you cannot
              remit, or not charging VAT you owe, are both expensive to unwind.
            </div>
          </div>
          <span style={S.pill(state.vat?.registered ? "good" : "neutral")}>
            {state.vat?.registered ? "VAT REGISTERED" : "NOT VAT REGISTERED"}
          </span>
        </div>

        <div style={S.factRow}>
          <div>
            <div style={S.factLabel}>Is Lusuku preparing your URA receipts?</div>
            <div style={S.factHelp}>
              URA now requires electronic receipts from restaurants and several
              other trades, whether or not you are VAT registered. When this is
              on, Lusuku prepares one for every order automatically from records
              it already keeps, so there is nothing for you to type. It is free
              and it is optional.
            </div>
          </div>
          <span style={S.pill(state.fiscalisation?.enabled ? "good" : "neutral")}>
            {state.fiscalisation?.enabled ? "ON" : "OFF"}
          </span>
        </div>
      </div>

      {/* ── FACT 3: the vendor's own two decisions ─────────────────────── */}
      <div>
        <label style={S.label} htmlFor="vendor-tax-tin">
          Your TIN (Taxpayer Identification Number)
        </label>
        <input
          id="vendor-tax-tin"
          type="text"
          maxLength={32}
          value={tin}
          onChange={(e) => setTin(e.target.value)}
          placeholder="e.g. 1000123456"
          disabled={!canEdit}
          style={{ ...S.input, ...(canEdit ? {} : { background: C.soft, color: C.light }) }}
        />
        <div style={{ ...S.note, marginTop: 6, maxWidth: 460 }}>
          {state.efris?.tinSource === "VAT" ? (
            <>
              We have filled this in from your VAT registration. Save it to
              confirm it is the taxpayer your receipts should be issued under.
            </>
          ) : (
            <>
              This is the number your receipts are issued under. It is not the
              same field as your VAT number, though for most businesses it is
              the same digits.
            </>
          )}
        </div>
      </div>

      <div>
        <div style={S.label}>Who sends your receipts to URA?</div>
        <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
          <label style={S.choice(remittance === "VENDOR_SELF")}>
            <input
              type="radio"
              name="vendor-tax-remittance"
              checked={remittance === "VENDOR_SELF"}
              onChange={() => setRemittance("VENDOR_SELF")}
              disabled={!canEdit}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={S.choiceTitle}>I file them myself</span>
              <span style={S.choiceBody}>
                Lusuku prepares every receipt and keeps the records, and you or
                your accountant submit them. Nothing is sent to URA on your
                behalf. This is the default.
              </span>
            </span>
          </label>

          <label style={S.choice(remittance === "LUSUKU_ON_BEHALF")}>
            <input
              type="radio"
              name="vendor-tax-remittance"
              checked={remittance === "LUSUKU_ON_BEHALF"}
              onChange={() => setRemittance("LUSUKU_ON_BEHALF")}
              disabled={!canEdit}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={S.choiceTitle}>Lusuku sends them for me</span>
              <span style={S.choiceBody}>
                Each completed order is submitted to URA under your TIN, as it
                happens. You can see everything that was sent, and you can switch
                back at any time. The tax owed is exactly the same either way:
                this only decides who presses send. The obligation stays yours,
                so it is worth checking your statements.
              </span>
            </span>
          </label>
        </div>
        {remittance === "LUSUKU_ON_BEHALF" && !tin.trim() ? (
          <div style={{ ...S.note, marginTop: 8, color: C.warn }}>
            Add your TIN above first. It is the taxpayer the receipts would be
            filed under, and we will not send anything without it.
          </div>
        ) : null}
      </div>

      {!canEdit ? (
        <div style={{ ...S.note, color: C.warn }}>
          You can see these settings but not change them. The TIN and the
          decision about who files with URA belong to whoever owns this Lusuku
          account.
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button
          type="button"
          style={S.btn(!dirty || saving || !canEdit)}
          disabled={!dirty || saving || !canEdit}
          onClick={save}
        >
          {saving ? "Saving…" : "Save tax settings"}
        </button>
        {state.efris?.remittanceAuthorisedAt ? (
          <span style={S.note}>
            You asked Lusuku to file on your behalf on{" "}
            {new Date(state.efris.remittanceAuthorisedAt).toLocaleDateString()}.
          </span>
        ) : null}
      </div>

      {/* FACT 4, read-only: how much of the catalogue carries URA codes. Mounted
          here, once, so every vendor type's Tax and receipts tab shows it
          without four call sites. Lusuku staff set the codes; the panel says so. */}
      <VendorFiscalCoveragePanel />
    </div>
  );
}
